/**
 * Hook for managing knowledge base
 * Handles document uploads, search, viewing, and deletion
 */

import { useState, useCallback } from 'react';
import { api } from '../../../utils/api';
import toast from 'react-hot-toast';
import { KnowledgeDocument, KnowledgeSearchResult, KnowledgeStats } from '../types';

export interface UseKnowledgeManagementReturn {
  documents: KnowledgeDocument[];
  searchResults: KnowledgeSearchResult[];
  stats: KnowledgeStats | null;
  isUploading: boolean;
  isSearching: boolean;
  loading: boolean;
  error: string | null;
  selectedDocument: KnowledgeDocument | null;
  uploadDocument: (file: File) => Promise<boolean>;
  searchKnowledge: (query: string, topK?: number) => Promise<KnowledgeSearchResult[]>;
  viewDocument: (docId: string) => Promise<string | null>;
  deleteDocument: (docId: string) => Promise<boolean>;
  loadDocuments: (assistantId?: string) => Promise<void>;
  loadStats: (assistantId?: string) => Promise<void>;
  clearSearch: () => void;
  selectDocument: (doc: KnowledgeDocument | null) => void;
  getKnowledgeStats: (assistantId?: string) => Promise<KnowledgeStats | null>;
}

export const useKnowledgeManagement = (): UseKnowledgeManagementReturn => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [searchResults, setSearchResults] = useState<KnowledgeSearchResult[]>([]);
  const [stats, setStats] = useState<KnowledgeStats | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<KnowledgeDocument | null>(null);

  /**
   * Upload a document to the knowledge base
   */
  const uploadDocument = useCallback(async (file: File): Promise<boolean> => {
    if (!file) {
      toast.error('No file selected');
      return false;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/knowledge/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success) {
        toast.success('Document uploaded successfully');

        // Add to documents list if returned
        if (response.data.document) {
          setDocuments(prev => [...prev, response.data.document]);
        }

        return true;
      } else {
        throw new Error('Upload failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload document';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to upload document:', err);
      return false;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Search the knowledge base
   */
  const searchKnowledge = useCallback(async (
    query: string,
    topK: number = 5
  ): Promise<KnowledgeSearchResult[]> => {
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return [];
    }

    setIsSearching(true);
    setError(null);

    try {
      const response = await api.post('/knowledge/search', {
        query: query.trim(),
        topK
      });

      if (response.data?.success && response.data?.results) {
        const results = response.data.results.map((r: any) => ({
          documentId: r.documentId || r.id,
          documentName: r.documentName || r.name || 'Unknown Document',
          snippet: r.snippet || r.content || '',
          score: r.score || 0
        })) as KnowledgeSearchResult[];

        setSearchResults(results);

        if (results.length === 0) {
          toast('No results found');
        } else {
          toast.success(`Found ${results.length} result(s)`);
        }

        return results;
      } else {
        throw new Error('Invalid search response');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Search failed';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Knowledge search failed:', err);
      return [];
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * View a document by ID (returns URL or content)
   */
  const viewDocument = useCallback(async (docId: string): Promise<string | null> => {
    if (!docId) {
      toast.error('Document ID is required');
      return null;
    }

    setError(null);

    try {
      const response = await api.get(`/knowledge/doc/${docId}`);

      if (response.data?.success && response.data?.url) {
        return response.data.url;
      } else if (response.data?.content) {
        return response.data.content;
      } else {
        throw new Error('Document not found');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to view document';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to view document:', err);
      return null;
    }
  }, []);

  /**
   * Delete a document from the knowledge base
   */
  const deleteDocument = useCallback(async (docId: string): Promise<boolean> => {
    if (!docId) {
      toast.error('Document ID is required');
      return false;
    }

    setError(null);

    try {
      const response = await api.delete(`/knowledge/documents/${docId}`);

      if (response.data?.success) {
        toast.success('Document deleted successfully');

        // Remove from local state
        setDocuments(prev => prev.filter(doc => doc.id !== docId));

        return true;
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete document';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to delete document:', err);
      return false;
    }
  }, []);

  /**
   * Load all documents for an assistant
   */
  const loadDocuments = useCallback(async (assistantId?: string) => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = assistantId
        ? `/knowledge/documents?assistantId=${assistantId}`
        : '/knowledge/documents';

      const response = await api.get(endpoint);

      if (response.data?.success && response.data?.documents) {
        setDocuments(response.data.documents);
      } else if (response.data?.documents) {
        // Handle case where success flag might not be present
        setDocuments(response.data.documents);
      } else {
        setDocuments([]);
      }
    } catch (err: any) {
      // Silently handle - might be no documents yet
      console.log('No documents found or error loading documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load knowledge base statistics
   */
  const loadStats = useCallback(async (assistantId?: string) => {
    setError(null);

    try {
      const endpoint = assistantId
        ? `/knowledge/stats?assistantId=${assistantId}`
        : '/knowledge/stats';

      const response = await api.get(endpoint);

      if (response.data?.success && response.data?.stats) {
        setStats(response.data.stats);
      } else if (response.data) {
        // Handle case where stats are returned directly
        setStats({
          totalDocuments: response.data.totalDocuments || 0,
          totalSize: response.data.totalSize || 0,
          lastUpdated: response.data.lastUpdated || new Date()
        });
      }
    } catch (err: any) {
      // Silently handle - stats might not be available
      console.log('No stats available:', err);
      setStats(null);
    }
  }, []);

  /**
   * Clear search results
   */
  const clearSearch = useCallback(() => {
    setSearchResults([]);
  }, []);

  /**
   * Select a document for viewing
   */
  const selectDocument = useCallback((doc: KnowledgeDocument | null) => {
    setSelectedDocument(doc);
  }, []);

  /**
   * Get knowledge base statistics
   */
  const getKnowledgeStats = useCallback(async (assistantId?: string): Promise<KnowledgeStats | null> => {
    try {
      const endpoint = assistantId
        ? `/knowledge/stats?assistantId=${assistantId}`
        : '/knowledge/stats';
      const response = await api.get(endpoint);
      if (response.data?.stats) {
        return response.data.stats;
      }
      return null;
    } catch (err) {
      console.warn('Failed to fetch knowledge stats:', err);
      return null;
    }
  }, []);

  return {
    documents,
    searchResults,
    stats,
    isUploading,
    isSearching,
    loading,
    error,
    selectedDocument,
    uploadDocument,
    searchKnowledge,
    viewDocument,
    deleteDocument,
    loadDocuments,
    loadStats,
    clearSearch,
    selectDocument,
    getKnowledgeStats
  };
};
